import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Clock, GripVertical, MoveHorizontal, CheckCircle } from 'lucide-react';
import axios from 'axios';

const WritingTestEnvironment = ({ isGuest = false }) => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [activeTask, setActiveTask] = useState(1);
  const [answers, setAnswers] = useState({ task1: '', task2: '' });
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showTabSwitchWarning, setShowTabSwitchWarning] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [redirectCountdown, setRedirectCountdown] = useState(7);
  const intervalRef = useRef(null);
  const autoSaveRef = useRef(null);
  const syncTimerRef = useRef(null);
  const blinkTriggeredRef = useRef(false);
  const testDurationMinutes = useRef(60);
  const [wordCounts, setWordCounts] = useState({ task1: 0, task2: 0 });
  const [splitPosition, setSplitPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [hasBlinkTriggered, setHasBlinkTriggered] = useState(false);
  const answersRef = useRef({ task1: '', task2: '' });
  const lastSavedRef = useRef({ task1: '', task2: '' });
  const isSubmittedRef = useRef(false);

  useEffect(() => {
    fetchSubmissionDetails();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
      if (syncTimerRef.current) clearInterval(syncTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  // Keep answersRef in sync with answers state
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // Ref to track tab switch count for warning system
  const tabSwitchCountRef = useRef(0);
  const isSubmittingRef = useRef(false);
  
  // CRITICAL: Tab switch detection and auto-submit with WARNING SYSTEM
  useEffect(() => {
    // Direct submit function that doesn't rely on state closures
    const forceSubmitExam = async () => {
      if (isSubmittedRef.current || isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      
      const currentAnswers = answersRef.current;
      
      console.log('[FORCE-SUBMIT] Starting submit with answers:', {
        task1Length: currentAnswers.task1?.length || 0,
        task2Length: currentAnswers.task2?.length || 0
      });
      
      try {
        // First, save answers via regular axios (more reliable than sendBeacon)
        const saveEndpoint = isGuest
          ? `/public/submission/${submissionId}`
          : `/student/submissions/${submissionId}`;
        
        await axios.put(saveEndpoint, {
          task1_answer: currentAnswers.task1,
          task2_answer: currentAnswers.task2
        });
        console.log('[FORCE-SUBMIT] Answers saved successfully');
        
        // Then submit
        const submitEndpoint = isGuest
          ? `/public/submission/${submissionId}/submit`
          : `/student/submissions/${submissionId}/force-submit`;
        
        await axios.post(submitEndpoint, {
          task1_answer: currentAnswers.task1,
          task2_answer: currentAnswers.task2
        });
        
        console.log('[FORCE-SUBMIT] Test submitted successfully');
        isSubmittedRef.current = true;
        
        // CRITICAL: Stop all timers and intervals
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
        if (syncTimerRef.current) clearInterval(syncTimerRef.current);
        
        // Show success modal
        setShowSuccessModal(true);
        
        // Start countdown and redirect
        let countdown = 7;
        setRedirectCountdown(countdown);
        const countdownInterval = setInterval(() => {
          countdown--;
          setRedirectCountdown(countdown);
          if (countdown <= 0) {
            clearInterval(countdownInterval);
            window.location.href = 'https://radianceedu.com';
          }
        }, 1000);
        
      } catch (error) {
        console.error('[FORCE-SUBMIT] Error:', error);
        isSubmittingRef.current = false;
        
        // If submit failed, try sendBeacon as last resort
        const baseURL = axios.defaults.baseURL || '';
        const fullBaseURL = baseURL.startsWith('http') ? baseURL : `${window.location.origin}${baseURL}`;
        const submitEndpoint = isGuest
          ? `${fullBaseURL}/public/submission/${submissionId}/submit`
          : `${fullBaseURL}/student/submissions/${submissionId}/force-submit`;
        
        const data = JSON.stringify({
          task1_answer: currentAnswers.task1,
          task2_answer: currentAnswers.task2
        });
        
        if (navigator.sendBeacon) {
          const blob = new Blob([data], { type: 'application/json' });
          navigator.sendBeacon(submitEndpoint, blob);
          console.log('[FORCE-SUBMIT] Fallback sendBeacon attempted');
        }
        
        isSubmittedRef.current = true;
        
        // Still show success and redirect (data was likely saved)
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
        setShowSuccessModal(true);
        
        let countdown = 7;
        setRedirectCountdown(countdown);
        const countdownInterval = setInterval(() => {
          countdown--;
          setRedirectCountdown(countdown);
          if (countdown <= 0) {
            clearInterval(countdownInterval);
            window.location.href = 'https://radianceedu.com';
          }
        }, 1000);
      }
    };

    const handleVisibilityChange = () => {
      // Don't trigger if already submitted or submitting
      if (isSubmittedRef.current || isSubmittingRef.current) return;
      
      if (document.visibilityState === 'hidden') {
        tabSwitchCountRef.current += 1;
        console.log('[TAB-SWITCH] Tab switched, count:', tabSwitchCountRef.current);
        
        // First tab switch: Just show warning (when they return)
        if (tabSwitchCountRef.current === 1) {
          setTabSwitchCount(1);
          setShowTabSwitchWarning(true);
          toast.error('⚠️ Warning! Tab switch detected. Next time your exam will be auto-submitted!', {
            duration: 10000
          });
        } 
        // Second+ tab switch: Auto-submit
        else {
          console.log('[TAB-SWITCH] Second tab switch - auto-submitting');
          toast.error('🚫 Second tab switch! Auto-submitting your exam...', {
            duration: 5000
          });
          forceSubmitExam();
        }
      }
    };

    const handleBeforeUnload = (e) => {
      if (isSubmittedRef.current) return;
      
      // Save progress before leaving (but don't submit)
      const currentAnswers = answersRef.current;
      const saveEndpoint = isGuest
        ? `/public/submission/${submissionId}`
        : `/student/submissions/${submissionId}`;
      
      // Try to save via sendBeacon
      const baseURL = axios.defaults.baseURL || '';
      const fullBaseURL = baseURL.startsWith('http') ? baseURL : `${window.location.origin}${baseURL}`;
      const data = JSON.stringify({
        task1_answer: currentAnswers.task1,
        task2_answer: currentAnswers.task2
      });
      
      if (navigator.sendBeacon) {
        const blob = new Blob([data], { type: 'application/json' });
        navigator.sendBeacon(`${fullBaseURL}${saveEndpoint.replace('/api', '')}`, blob);
        console.log('[BEFORE-UNLOAD] Progress saved via sendBeacon');
      }
      
      // Show warning
      e.preventDefault();
      e.returnValue = '⚠️ WARNING: Leaving will save your progress. Make sure to return and submit before time expires!';
      return e.returnValue;
    };

    // Only add listeners if we have a valid submission
    if (submissionId) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      // REMOVED: blur event - too sensitive, causes false positives
      console.log('[ANTI-CHEAT] Listeners added for submission:', submissionId);
    }
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [submissionId, isGuest]);

  useEffect(() => {
    if (submission && timeRemaining > 0) {
      startTimer();
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission]);

  // Auto-save every 10 seconds (reduced from 30 for better data protection)
  useEffect(() => {
    if (submission) {
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
      autoSaveRef.current = setTimeout(() => {
        saveProgress();
      }, 10000); // 10 seconds
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, submission]);

  const fetchSubmissionDetails = async () => {
    try {
      const endpoint = isGuest 
        ? `/public/submission/${submissionId}`
        : `/student/submissions/${submissionId}`;
      const response = await axios.get(endpoint);
      const submissionData = response.data;
      
      // CRITICAL: Check if already submitted
      if (submissionData.status === 'submitted' || submissionData.status === 'marked' || submissionData.status === 'published') {
        toast.error('This test has already been submitted.');
        isSubmittedRef.current = true;
        // Redirect after showing message
        setTimeout(() => {
          window.location.href = 'https://radianceedu.com';
        }, 2000);
        return;
      }
      
      setSubmission(submissionData);
      setAnswers({
        task1: submissionData.task1_answer || '',
        task2: submissionData.task2_answer || ''
      });
      
      // Set active task based on configuration
      if (submissionData.test_details.task_configuration === 'task_2_only') {
        setActiveTask(2);
      } else {
        setActiveTask(1); // Default to task 1 for 'full' and 'task_1_only'
      }
      
      // Use server-calculated remaining time (independent of client clock)
      const serverTimeRemaining = submissionData.server_time_remaining;
      if (serverTimeRemaining !== undefined) {
        setTimeRemaining(serverTimeRemaining);
      } else {
        // Fallback to old calculation if server doesn't provide it
        const startTime = new Date(submissionData.attempt_started_at);
        const durationMinutes = submissionData.test_details.duration_minutes;
        const durationMs = durationMinutes * 60 * 1000;
        const elapsed = Date.now() - startTime.getTime();
        const remaining = Math.max(0, durationMs - elapsed);
        setTimeRemaining(Math.floor(remaining / 1000));
      }
      
      testDurationMinutes.current = submissionData.test_details.duration_minutes;
      
      setWordCounts({
        task1: countWords(submissionData.task1_answer || ''),
        task2: countWords(submissionData.task2_answer || '')
      });
      
    } catch (error) {
      toast.error('Failed to load test details');
      navigate(isGuest ? '/' : '/student');
    } finally {
      setLoading(false);
    }
  };

  const syncTimeWithServer = async () => {
    try {
      const endpoint = isGuest 
        ? `/public/submission/${submissionId}`
        : `/student/submissions/${submissionId}`;
      const response = await axios.get(endpoint);
      const serverTimeRemaining = response.data.server_time_remaining;
      
      if (serverTimeRemaining !== undefined) {
        // Sync with server time (with small tolerance to avoid flickering)
        setTimeRemaining(prev => {
          const diff = Math.abs(prev - serverTimeRemaining);
          // Only update if difference is more than 3 seconds (accounts for network delay)
          if (diff > 3) {
            console.log('Timer synced with server:', serverTimeRemaining);
            return serverTimeRemaining;
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Timer sync failed:', error);
    }
  };

  const startTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (syncTimerRef.current) clearInterval(syncTimerRef.current);
    
    // Client-side countdown (every second)
    intervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setIsTimeUp(true);
          handleSubmit(true);
          return 0;
        }
        
        // Dynamic warning: 10 minutes before end (600 seconds)
        const tenMinutesInSeconds = 600;
        if (prev === tenMinutesInSeconds && testDurationMinutes.current > 10) {
          toast.warning('10 minutes remaining!', { duration: 5000 });
        } else if (prev === 300 && testDurationMinutes.current > 5) {
          toast.warning('5 minutes remaining!', { duration: 5000 });
          // Trigger blink animation once
          if (!blinkTriggeredRef.current) {
            blinkTriggeredRef.current = true;
            setHasBlinkTriggered(true);
          }
        }
        
        return prev - 1;
      });
    }, 1000);
    
    // Server sync (every 2 minutes to prevent client drift)
    syncTimerRef.current = setInterval(() => {
      syncTimeWithServer();
    }, 120000); // 2 minutes
  };

  const countWords = (text) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleAnswerChange = (task, value) => {
    // CRITICAL: Update ref IMMEDIATELY before state update
    // This ensures answersRef always has the latest value even if React state is stale
    const newAnswers = { ...answersRef.current, [task]: value };
    answersRef.current = newAnswers;
    
    // Debug log for tracking
    console.log(`[ANSWER-CHANGE] Task ${task} updated, lengths:`, {
      task1: newAnswers.task1?.length || 0,
      task2: newAnswers.task2?.length || 0
    });
    
    setAnswers(prev => ({ ...prev, [task]: value }));
    setWordCounts(prev => ({ ...prev, [task]: countWords(value) }));
  };

  const saveProgress = async () => {
    // CRITICAL: Don't auto-save if already submitted
    if (!submission || saving || isSubmittedRef.current) return;
    
    // Check if there are actual changes to save
    const hasChanges = 
      answers.task1 !== lastSavedRef.current.task1 ||
      answers.task2 !== lastSavedRef.current.task2;
    
    if (!hasChanges) return; // Skip if no changes
    
    setSaving(true);
    try {
      const endpoint = isGuest
        ? `/public/submission/${submissionId}`
        : `/student/submissions/${submissionId}`;
      await axios.put(endpoint, {
        task1_answer: answers.task1,
        task2_answer: answers.task2
      });
      // Update lastSavedRef after successful save
      lastSavedRef.current = { task1: answers.task1, task2: answers.task2 };
      console.log('[AUTO-SAVE] Progress saved successfully');
    } catch (error) {
      console.error('Auto-save failed:', error);
      toast.error('Auto-save failed. Please check your connection.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (autoSubmit = false) => {
    if (submitting) return;

    setSubmitting(true);
    try {
      // Use answersRef for auto-submit to get latest values (closure issue with setInterval)
      const currentAnswers = autoSubmit ? answersRef.current : answers;
      
      // Step 1: Save final answers
      const saveEndpoint = isGuest
        ? `/public/submission/${submissionId}`
        : `/student/submissions/${submissionId}`;
      
      console.log('[SUBMIT] Step 1: Saving final answers...', { autoSubmit, task1Len: currentAnswers.task1?.length, task2Len: currentAnswers.task2?.length });
      await axios.put(saveEndpoint, {
        task1_answer: currentAnswers.task1,
        task2_answer: currentAnswers.task2
      });
      console.log('[SUBMIT] Step 1: Answers saved successfully');
      
      // Step 2: Submit the test (also send answers as backup)
      const submitEndpoint = isGuest
        ? `/public/submission/${submissionId}/submit`
        : `/student/submissions/${submissionId}`;
      
      console.log('[SUBMIT] Step 2: Submitting test...');
      if (isGuest) {
        // Send answers along with submit as backup
        await axios.post(submitEndpoint, {
          task1_answer: currentAnswers.task1,
          task2_answer: currentAnswers.task2
        });
      } else {
        await axios.put(submitEndpoint, {
          status: 'submitted',
          task1_answer: currentAnswers.task1,
          task2_answer: currentAnswers.task2
        });
      }
      console.log('[SUBMIT] Step 2: Test submitted successfully');
      
      // Mark as submitted to prevent further tab switch triggers
      isSubmittedRef.current = true;
      
      // Stop timer
      // CRITICAL: Stop all timers to prevent auto-save errors
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
      if (syncTimerRef.current) clearInterval(syncTimerRef.current);
      
      // Show success modal
      setShowSuccessModal(true);
      
      // Start countdown and redirect after 7 seconds
      let countdown = 7;
      setRedirectCountdown(countdown);
      const countdownInterval = setInterval(() => {
        countdown--;
        setRedirectCountdown(countdown);
        
        if (countdown <= 0) {
          clearInterval(countdownInterval);
          window.location.href = 'https://radianceedu.com';
        }
      }, 1000);
    } catch (error) {
      console.error('[SUBMIT] Error:', error);
      console.error('[SUBMIT] Error details:', error.response?.data || error.message);
      
      const errorMessage = error.response?.data?.detail || 'Failed to submit test. Please try again.';
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderFormattedText = (text) => {
    if (!text) return '';
    
    let formatted = text
      .replace(/\*\*([\s\S]+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([\s\S]+?)\*/g, '<em>$1</em>')
      .replace(/__([\s\S]+?)__/g, '<u>$1</u>')
      .replace(/\n/g, '<br/>');
    
    return <div dangerouslySetInnerHTML={{ __html: formatted }} />;
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const container = document.getElementById('split-container');
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
    
    if (newPosition > 20 && newPosition < 80) {
      setSplitPosition(newPosition);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading test...</div>
      </div>
    );
  }

  if (!submission) {
    return null;
  }

  // Determine current task based on configuration and active task
  const getCurrentTask = () => {
    if (submission?.test_details?.task_configuration === 'task_1_only') {
      return 'task1';
    } else if (submission?.test_details?.task_configuration === 'task_2_only') {
      return 'task2';
    } else {
      return activeTask === 1 ? 'task1' : 'task2';
    }
  };
  
  const currentTask = getCurrentTask();
  const currentPrompt = currentTask === 'task1'
    ? submission.test_details.task1_prompt 
    : submission.test_details.task2_prompt;
  const hasTask1Image = currentTask === 'task1' && submission.test_details.task1_image;

  return (
    <div className="h-screen flex flex-col bg-white" onMouseUp={handleMouseUp}>
      {/* Header */}
      <div className="header-gradient flex items-center justify-between px-6 py-3 shadow-lg">
        {/* Logo */}
        <div className="flex items-center space-x-4">
          <img 
            src="/radiance-logo.webp" 
            alt="Radiance Education"
            className="h-10 w-auto object-contain"
          />
        </div>
        
        {/* Test Info */}
        <div className="flex-1 flex items-center justify-center space-x-4">
          <h1 className="text-white text-lg font-semibold">
            {submission.test_details.title}
          </h1>
          <div className="text-white text-sm opacity-90">
            {submission.candidate_name} • {submission.candidate_email}
          </div>
        </div>
        
        {/* Timer */}
        <div 
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300 ${
            timeRemaining <= 600 
              ? 'bg-red-700 shadow-2xl scale-105' 
              : 'shadow-lg'
          }`}
          style={{
            backgroundColor: timeRemaining <= 600 ? undefined : 'rgb(88, 143, 151)',
            animation: timeRemaining === 300 && hasBlinkTriggered ? 'blink 0.8s' : 'none'
          }}
        >
          <Clock className={`${timeRemaining <= 600 ? 'w-6 h-6' : 'w-5 h-5'} text-white transition-all duration-300 flex-shrink-0`} />
          <div className="flex items-center gap-2">
            <span 
              className={`text-white transition-all duration-300 ${
                timeRemaining <= 600 
                  ? 'font-extrabold text-3xl' 
                  : 'font-bold text-2xl'
              }`}
              style={{ 
                minWidth: timeRemaining <= 600 ? '120px' : '100px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                letterSpacing: '0.02em'
              }}
            >
              {formatTime(timeRemaining)}
            </span>
            <span className={`text-white transition-all duration-300 ${
              timeRemaining <= 600 ? 'text-sm' : 'text-xs'
            } opacity-90 whitespace-nowrap`}>
              Minutes<br/>remaining
            </span>
          </div>
        </div>
      </div>

      {/* Split Content Area */}
      <div 
        id="split-container"
        className="flex-1 flex relative overflow-hidden"
        style={{ cursor: isDragging ? 'col-resize' : 'default' }}
      >
        {/* Question Panel (Left) */}
        <div 
          className="bg-slate-50 border-r border-slate-200 overflow-y-auto"
          style={{ width: `${splitPosition}%` }}
        >
          <div className="p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4">
              Task {currentTask === 'task1' ? '1' : '2'} Question
            </h2>
            <div className="text-sm text-slate-600 mb-4">
              <p className="mb-2">You should spend about {currentTask === 'task1' ? '20' : '40'} minutes on this task.</p>
              <p className="font-medium">Write at least {currentTask === 'task1' ? '150' : '250'} words.</p>
            </div>
            <div className="text-slate-700 leading-relaxed mb-4">
              {renderFormattedText(currentPrompt)}
            </div>
            {hasTask1Image && (
              <div className="mt-4 border rounded-lg p-4 bg-white">
                <img
                  src={submission.test_details.task1_image}
                  alt="Task 1 visual"
                  style={{
                    width: submission.test_details.task1_image_width ? `${submission.test_details.task1_image_width}px` : 'auto',
                    height: submission.test_details.task1_image_height ? `${submission.test_details.task1_image_height}px` : 'auto',
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                  className="mx-auto"
                />
              </div>
            )}
          </div>
        </div>

        {/* Resizable Divider with Icon */}
        <div
          className="w-2 cursor-col-resize transition-colors flex items-center justify-center group relative"
          style={{backgroundColor: 'rgb(22, 113, 127)'}}
          onMouseDown={handleMouseDown}
        >
          <div className="absolute inset-y-0 flex items-center justify-center">
            <div className="bg-white border-2 border-slate-300 rounded p-1" style={{padding: '5px'}}>
              <MoveHorizontal className="w-4 h-4 text-black" />
            </div>
          </div>
        </div>

        {/* Answer Panel (Right) */}
        <div 
          className="flex flex-col bg-white"
          style={{ width: `${100 - splitPosition - 0.5}%` }}
        >
          <div className="flex-1 p-6 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-slate-800">
                Your Answer
              </h3>
              <div className="text-sm font-medium text-slate-600">
                Words: <span className="text-blue-600">{wordCounts[currentTask]}</span>
              </div>
            </div>
            <textarea
              value={answers[currentTask]}
              onChange={(e) => handleAnswerChange(currentTask, e.target.value)}
              placeholder={`Write your Task ${currentTask === 'task1' ? '1' : '2'} answer here...`}
              className="flex-1 w-full p-4 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-base leading-relaxed"
              spellCheck={submission.test_details.type !== 'partial_test'}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              onPaste={(e) => {
                e.preventDefault();
                toast.error('Paste is disabled. Please type your answer manually.', { duration: 3000 });
              }}
              onCopy={(e) => {
                // Allow copy - students can copy their own text
              }}
            />
            <div className="mt-4 mb-6 flex items-center justify-between">
              <div className="text-xs text-slate-500">
                {saving ? 'Saving...' : 'Auto-saved'}
              </div>
              <Button
                onClick={() => setShowConfirmModal(true)}
                disabled={submitting}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-2.5 font-semibold"
              >
                {submitting ? 'Submitting...' : 'Submit Test'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal - Full Screen Glass Effect */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(15, 23, 42, 0.6)'
        }}>
          <div className="relative max-w-lg w-full animate-in zoom-in-95 fade-in duration-300">
            {/* Glass Card */}
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8 text-center">
              {/* Warning Icon */}
              <div className="mx-auto w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-6 shadow-lg">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>

              {/* Title */}
              <h2 className="text-3xl font-bold text-slate-800 mb-4">
                Submit Your Test?
              </h2>

              {/* Message */}
              <p className="text-lg text-slate-600 mb-6">
                Once submitted, you cannot make any changes to your answers.
              </p>

              {/* Word Count Info */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 mb-6 border border-blue-200">
                <div className="flex justify-around text-sm">
                  {submission?.test_details?.task_configuration !== 'task_2_only' && (
                    <div>
                      <p className="text-slate-600 font-medium">Task 1</p>
                      <p className="text-2xl font-bold text-blue-600">{wordCounts.task1}</p>
                      <p className="text-xs text-slate-500">words</p>
                    </div>
                  )}
                  {submission?.test_details?.task_configuration !== 'task_1_only' && (
                    <div>
                      <p className="text-slate-600 font-medium">Task 2</p>
                      <p className="text-2xl font-bold text-blue-600">{wordCounts.task2}</p>
                      <p className="text-xs text-slate-500">words</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-4">
                <Button
                  onClick={() => setShowConfirmModal(false)}
                  variant="outline"
                  className="flex-1 py-3 text-base font-semibold border-2 hover:bg-slate-50"
                  disabled={submitting}
                >
                  ← Review Again
                </Button>
                <Button
                  onClick={() => {
                    setShowConfirmModal(false);
                    handleSubmit(false);
                  }}
                  disabled={submitting}
                  className="flex-1 py-3 text-base font-semibold text-white shadow-lg"
                  style={{
                    background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
                    boxShadow: '0 4px 14px rgba(220, 38, 38, 0.4)'
                  }}
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Submitting...
                    </span>
                  ) : 'Confirm & Submit'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8 text-center animate-in fade-in zoom-in duration-300">
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4" style={{backgroundColor: 'rgb(22, 113, 127)'}}>
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3">
                {isTimeUp ? "Time's Up!" : "Test Submitted Successfully!"}
              </h2>
              <p className="text-slate-600 mb-2">
                {isTimeUp 
                  ? "Your answer has been submitted automatically." 
                  : "You will receive your results via email."}
              </p>
              <p className="text-slate-700 font-semibold">
                Thank you for taking the test!
              </p>
            </div>
            
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                Redirecting to Radiance Education in
              </p>
              <p className="text-3xl font-bold mt-2" style={{color: 'rgb(22, 113, 127)'}}>
                {redirectCountdown}s
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Conditional Task Tabs based on task_configuration */}
      {submission?.test_details?.task_configuration === 'full' && (
        <div className="border-t border-slate-200 bg-white flex shadow-lg">
          <button
            onClick={() => setActiveTask(1)}
            className={`flex-1 py-3 px-6 text-center font-medium transition-colors ${
              activeTask === 1
                ? 'text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            style={activeTask === 1 ? {backgroundColor: 'rgb(22, 113, 127)'} : {}}
          >
            Task 1
          </button>
          <button
            onClick={() => setActiveTask(2)}
            className={`flex-1 py-3 px-6 text-center font-medium transition-colors ${
              activeTask === 2
                ? 'text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            style={activeTask === 2 ? {backgroundColor: 'rgb(22, 113, 127)'} : {}}
          >
            Task 2
          </button>
        </div>
      )}
      
      {/* Single Task Header for task_1_only or task_2_only */}
      {submission?.test_details?.task_configuration === 'task_1_only' && (
        <div className="bg-teal-700 text-white py-3 px-6 text-center font-medium">
          Task 1
        </div>
      )}
      {submission?.test_details?.task_configuration === 'task_2_only' && (
        <div className="bg-teal-700 text-white py-3 px-6 text-center font-medium">
          Task 2
        </div>
      )}
    </div>
  );
};

export default WritingTestEnvironment;